import React, { createContext, useContext, useState, useEffect } from 'react';
import { healthApi } from '@/services/api';

interface PlatformSettings {
  platform_name: string;
  platform_description: string;
  platform_logo: string;
  platform_hero_image: string;
  platform_email: string;
  platform_phone: string;
  platform_address: string;
  platform_website: string;
  social_facebook: string;
  social_twitter: string;
  social_instagram: string;
  social_linkedin: string;
  social_youtube: string;
  social_tiktok: string;
  [key: string]: any;
}

interface PlatformSettingsContextType {
  settings: PlatformSettings | null;
  isLoading: boolean;
  refreshSettings: () => Promise<void>;
}

const PlatformSettingsContext = createContext<PlatformSettingsContextType | undefined>(undefined);

export const PlatformSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const res = await healthApi.getPlatformSettings();
      if (res.success) {
        setSettings(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch platform settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return (
    <PlatformSettingsContext.Provider value={{ settings, isLoading, refreshSettings: fetchSettings }}>
      {children}
    </PlatformSettingsContext.Provider>
  );
};

export const usePlatformSettings = () => {
  const context = useContext(PlatformSettingsContext);
  if (context === undefined) {
    throw new Error('usePlatformSettings must be used within a PlatformSettingsProvider');
  }
  return context;
};
